import { useDraggable } from '@dnd-kit/core';
import { Clock } from 'lucide-react';
import { Chip, KanbanCard as KanbanCardShell } from '@/ui/primitives';
import { cn } from '@/lib/utils';
import type { CardFieldMap, KanbanCard as Card } from '../types';

interface Props {
  card: Card;
  fieldMap: CardFieldMap;
  onSelect: () => void;
  muted?: boolean;
}

const SLA_TONE = {
  critico: 'danger',
  atencao: 'warn',
  ok: 'ok',
} as const;

// Tailwind explícito reproduzindo .k-card de ui/styles/legacy.css:
// bg-elevated, border 1px, radius 8px, padding 12px 14px,
// shadow-1 base, hover shadow-2 + translateY(-1px) + border-border2.
export function KanbanCard({ card, fieldMap, onSelect, muted = false }: Props) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: card.id,
    data: { card },
  });
  const fields = fieldMap(card);
  const slaRaw = card.sla_status;
  const sla = slaRaw && slaRaw in SLA_TONE ? (slaRaw as keyof typeof SLA_TONE) : null;

  // DS: card sem bordas coloridas. SLA é comunicado via Chip dentro do card.
  const style: React.CSSProperties | undefined = transform
    ? {
        transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
        zIndex: 50,
      }
    : undefined;

  function handleClick(e: React.MouseEvent) {
    if (isDragging) {
      e.preventDefault();
      return;
    }
    onSelect();
  }

  return (
    <KanbanCardShell
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      onClick={handleClick}
      className={cn(
        'relative cursor-grab select-none active:cursor-grabbing',
        muted && 'bg-surface3/70 grayscale opacity-70 hover:opacity-85',
        isDragging && 'opacity-40 shadow-lg',
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <span
          className="min-w-0 font-semibold text-text leading-snug"
          style={{ fontSize: '0.85rem' }}
        >
          <span className="line-clamp-2">
            {fields.title}{' '}
            <span className="whitespace-nowrap font-medium text-text-muted">#{card.id}</span>
          </span>
        </span>
        {(fields.logo || (sla && SLA_TONE[sla])) && (
          <div className="flex items-center gap-1.5 shrink-0">
            {sla && SLA_TONE[sla] && (
              <Chip tone={SLA_TONE[sla]} size="sm" dot>
                SLA
              </Chip>
            )}
            {fields.logo}
          </div>
        )}
      </div>

      {fields.subtitle && (
        <span className="text-text-2 line-clamp-1" style={{ fontSize: '0.78rem' }}>
          {fields.subtitle}
        </span>
      )}

      {fields.chips && fields.chips.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-1">
          {fields.chips
            .filter((c) => c.value)
            .map((c, i) => (
              <Chip
                key={`${c.label}-${i}`}
                tone={c.tone ?? 'neutral'}
                size="sm"
                className="max-w-full truncate"
                title={String(c.value)}
              >
                {c.value}
              </Chip>
            ))}
        </div>
      )}

      <div
        className="flex items-center justify-between text-text-muted mt-1"
        style={{ fontSize: '0.7rem' }}
      >
        {fields.meta && <span className="truncate">{fields.meta}</span>}
        {card.dias_na_coluna != null && (
          <span className="inline-flex items-center gap-1 shrink-0">
            <Clock size={11} strokeWidth={1.5} />
            {card.dias_na_coluna}d
          </span>
        )}
      </div>
    </KanbanCardShell>
  );
}
